import {
  SxProps,
  TextField,
  TextFieldVariants,
} from "@mui/material";
import AddressAutocomplete, { AddressAutocompleteValue } from "mui-address-autocomplete";
import { useState } from "react";

import { AddressData, addressFromGoogleResponse, emptyAddress } from "@caredna-marlin/utils/client/geocoding";
import { useOrgConfig } from "~/providers/OrgConfigProvider";

type AddressFormProps = {
  address: AddressData;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  setAddress: (newAddress: AddressData) => void;
  skipLineTwo?: boolean;
  sx?: SxProps;
  variant?: TextFieldVariants;
  label?: string;
  size?: "small"
};

const AddressForm = ({
  address,
  disabled,
  error,
  required,
  setAddress,
  skipLineTwo,
  sx,
  size,
  variant = "standard",
  label = "Address",
}: AddressFormProps) => {
  const orgConfig = useOrgConfig();

  const [addressInternal, setAddressInternal] = useState<AddressAutocompleteValue | null>({
    place_id: "",
    description: [address.addressStreet, address.addressCity, address.addressState?.replace("  ", "")].filter(Boolean).join(', '),
    components: {},
    structured_formatting: {
      main_text: address.addressStreet || "",
      secondary_text: [address.addressCity, address.addressState?.replace("  ", "")].filter(Boolean).join(', '),
      main_text_matched_substrings: []
    }
  });

  return <>
    <AddressAutocomplete
      apiKey={orgConfig.GOOGLE_MAPS_WEB_API_KEY}
      label={label}
      value={addressInternal}
      disabled={disabled}
      sx={sx}
      onChange={(_, value) => {
        setAddressInternal(value);
        setAddress(value && value.place_id ? addressFromGoogleResponse(value) : emptyAddress());
      }}
      renderInput={params => <TextField
        {...params}
        fullWidth
        label={label}
        variant={variant}
        error={error}
        margin={size === "small" ? "dense" : "normal"}
        required={required}
        helperText={error ? "Address not found" : undefined}
        size={size}
        slotProps={{
          htmlInput: {
            ...params.inputProps,
            ...(addressInternal?.place_id ? {"data-address-place-id": addressInternal.place_id} : {})
          }
        }}
      />}
    />
    {!skipLineTwo && <TextField
      fullWidth
      label="Apt, Suite, Unit, Building, Floor, etc."
      margin="none"
      // `size` reached the autocomplete and not this field, so a caller asking
      // for a small form got one input a size larger than the rest of it.
      size={size}
      variant={variant}
      autoCorrect="off"
      value={address.addressLineTwo || ""}
      onChange={(e) => setAddress({...address, addressLineTwo: e.target.value })}
      disabled={disabled}
    />}
  </>
};

export default AddressForm;
